<template>
  <div class="receipt-custom-print">
    <div class="receipt-page-head">
      <div>
        <h2 class="receipt-page-title">小票打印</h2>
        <p class="receipt-page-desc">
          选择打印机后填写内容即可出票。常用字段可「保存默认内容」，下次自动填入。
        </p>
      </div>
      <div class="header-actions">
        <el-input-number v-model="copies" :min="1" :max="10" size="small" style="width: 110px;" />
        <el-button class="btn-secondary" :loading="savingDefaults" @click="saveDefaults">保存默认内容</el-button>
        <el-button class="btn-secondary" :loading="previewLoading" @click="openPreview">预览小票</el-button>
        <el-button
          v-if="activePrinter === 'jolimark'"
          class="btn-secondary"
          :loading="browserLoading"
          @click="browserPrint"
        >浏览器打印</el-button>
        <el-button
          v-if="activePrinter === 'jolimark'"
          class="btn-primary"
          :loading="printing"
          @click="cloudPrint('jolimark')"
        >映美云打印</el-button>
        <el-button
          v-else
          type="success"
          class="btn-feie"
          :loading="printing"
          @click="cloudPrint('feie')"
        >飞鹅云打印</el-button>
      </div>
    </div>

    <el-tabs v-model="activePrinter" class="printer-tabs" @tab-click="onPrinterChange">
      <el-tab-pane label="映美云打印" name="jolimark" />
      <el-tab-pane label="飞鹅云打印" name="feie" />
    </el-tabs>

    <el-alert
      :type="activePrinter === 'feie' ? 'success' : 'info'"
      :closable="false"
      show-icon
      :title="activePrinter === 'feie' ? '飞鹅云 · 零售/厨房单' : '映美云 · 随心订发货单'"
      :description="alertDesc"
      class="receipt-alert"
    />

    <section class="receipt-panel">
      <header class="panel-head">
        <h3>填写小票内容</h3>
        <el-button type="text" size="mini" @click="restoreDefaults">恢复默认内容</el-button>
      </header>
      <el-form label-width="108px" size="small" class="print-form">
        <el-row :gutter="16">
          <el-col v-for="field in formFields" :key="field.key" :xs="24" :sm="12" :md="8">
            <el-form-item :label="field.label">
              <el-input
                v-if="field.inputType === 'textarea'"
                v-model="fieldValues[field.key]"
                type="textarea"
                :rows="2"
                :placeholder="field.placeholder"
              />
              <el-input-number
                v-else-if="field.inputType === 'number'"
                v-model="fieldValues[field.key]"
                :min="1"
                :max="999"
                controls-position="right"
                style="width: 100%;"
              />
              <el-input
                v-else
                v-model="fieldValues[field.key]"
                :placeholder="field.placeholder"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </section>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'
import { getAdminToken } from '../utils/auth'
import {
  RECEIPT_FIELD_META,
  FEIE_FIELD_META,
  RECEIPT_PRINT_FIELD_KEYS,
  FEIE_PRINT_FIELD_KEYS,
  RECEIPT_PRINT_BUILTIN_DEFAULTS,
  FEIE_PRINT_BUILTIN_DEFAULTS,
  defaultReceiptFieldValues,
  formatChinaDateTime
} from '../constants/receiptFieldMeta'

export default {
  name: 'ReceiptCustomPrint',
  data() {
    return {
      activePrinter: 'jolimark',
      fieldValues: defaultReceiptFieldValues(),
      jolimarkDefaults: { ...RECEIPT_PRINT_BUILTIN_DEFAULTS },
      feieDefaults: { ...FEIE_PRINT_BUILTIN_DEFAULTS },
      jolimarkLabels: {},
      feieLabels: {},
      copies: 1,
      savingDefaults: false,
      previewLoading: false,
      browserLoading: false,
      printing: false
    }
  },
  computed: {
    printKeys() {
      return this.activePrinter === 'feie' ? FEIE_PRINT_FIELD_KEYS : RECEIPT_PRINT_FIELD_KEYS
    },
    fieldMeta() {
      return this.activePrinter === 'feie' ? FEIE_FIELD_META : RECEIPT_FIELD_META
    },
    fieldLabels() {
      return this.activePrinter === 'feie' ? this.feieLabels : this.jolimarkLabels
    },
    savedDefaults() {
      return this.activePrinter === 'feie' ? this.feieDefaults : this.jolimarkDefaults
    },
    formFields() {
      return this.fieldMeta
        .filter((f) => this.printKeys.includes(f.key))
        .map((f) => ({
          key: f.key,
          inputType: f.inputType || 'text',
          label: this.fieldLabels[f.key] || f.labelZh,
          placeholder: f.labelZh
        }))
    },
    alertDesc() {
      return this.activePrinter === 'feie'
        ? '适合非随心订零售单。留空字段不会打印。进单支付成功后也会自动飞鹅出单。'
        : '适合随心订发货。订单号、取货方式等已有默认文案；只需填写收货人、地址等变动项。'
    }
  },
  mounted() {
    this.loadConfigAndDefaults()
  },
  methods: {
    onPrinterChange() {
      this.applyDefaults(this.savedDefaults)
    },
    mergeSavedDefaults(raw, builtin) {
      const base = { ...builtin }
      const src = raw && typeof raw === 'object' ? raw : {}
      for (const key of Object.keys(base).concat(Object.keys(src))) {
        if (src[key] == null || src[key] === '') continue
        base[key] = key === 'packageQty' ? Math.max(1, Number(src[key]) || 1) : String(src[key]).trim()
      }
      return base
    },
    applyDefaults(defaults) {
      const next = defaultReceiptFieldValues()
      const src = defaults && typeof defaults === 'object' ? defaults : {}
      for (const key of this.printKeys) {
        if (key === 'printTime') continue
        if (src[key] != null && src[key] !== '') next[key] = src[key]
      }
      next.printTime = formatChinaDateTime(new Date())
      if (!next.packageQty) next.packageQty = 1
      this.fieldValues = next
    },
    async loadConfigAndDefaults() {
      try {
        const resp = await this.$api.get('/admin/receipt-config')
        if (resp && resp.code === 200 && resp.data) {
          const d = resp.data
          if (d.fields) {
            const labels = {}
            for (const key of RECEIPT_PRINT_FIELD_KEYS) {
              const row = d.fields[key]
              labels[key] = row && row.label ? String(row.label) : undefined
            }
            this.jolimarkLabels = labels
          }
          this.jolimarkDefaults = this.mergeSavedDefaults(
            d.custom_print_defaults,
            RECEIPT_PRINT_BUILTIN_DEFAULTS
          )
          const fe = d.feie || {}
          if (fe.fields) {
            const labels = {}
            for (const key of FEIE_PRINT_FIELD_KEYS) {
              const row = fe.fields[key]
              labels[key] = row && row.label ? String(row.label) : undefined
            }
            this.feieLabels = labels
          }
          this.feieDefaults = this.mergeSavedDefaults(
            fe.custom_print_defaults,
            FEIE_PRINT_BUILTIN_DEFAULTS
          )
        }
      } catch (_e) {
        this.jolimarkDefaults = { ...RECEIPT_PRINT_BUILTIN_DEFAULTS }
        this.feieDefaults = { ...FEIE_PRINT_BUILTIN_DEFAULTS }
      }
      this.applyDefaults(this.savedDefaults)
    },
    restoreDefaults() {
      this.applyDefaults(this.savedDefaults)
      this.$message.success('已恢复默认内容')
    },
    buildDefaultsPayload() {
      const out = {}
      for (const key of this.printKeys) {
        if (key === 'printTime') continue
        const val = this.fieldValues[key]
        if (val == null || val === '') continue
        out[key] = typeof val === 'number' ? val : String(val).trim()
      }
      return out
    },
    async saveDefaults() {
      this.savingDefaults = true
      try {
        const defaults = this.buildDefaultsPayload()
        const printer = this.activePrinter
        const resp = await this.$api.put(
          `/admin/receipt-config/print-defaults?printer=${printer}`,
          { defaults, printer }
        )
        if (resp && resp.code === 200) {
          if (printer === 'feie') {
            this.feieDefaults = this.mergeSavedDefaults(resp.data || defaults, FEIE_PRINT_BUILTIN_DEFAULTS)
          } else {
            this.jolimarkDefaults = this.mergeSavedDefaults(
              resp.data || defaults,
              RECEIPT_PRINT_BUILTIN_DEFAULTS
            )
          }
          this.$message.success(resp.message || '默认内容已保存')
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.savingDefaults = false
      }
    },
    buildPayloadFields() {
      const out = {}
      for (const key of this.printKeys) {
        const val = this.fieldValues[key]
        if (val == null || val === '') continue
        out[key] = typeof val === 'number' ? val : String(val).trim()
      }
      return out
    },
    apiBase() {
      return (this.$api && this.$api.defaults && this.$api.defaults.baseURL) || '/api'
    },
    authHeaders() {
      const token = getAdminToken()
      const headers = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`
      return headers
    },
    async fetchHtml(path) {
      const resp = await fetch(`${this.apiBase().replace(/\/$/, '')}${path}`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({
          fields: this.buildPayloadFields(),
          printer: this.activePrinter
        })
      })
      if (!resp.ok) {
        const text = await resp.text()
        throw new Error(text || `请求失败 (${resp.status})`)
      }
      return resp.text()
    },
    openHtmlWindow(html) {
      const w = window.open('', '_blank')
      if (!w) {
        this.$message.warning('请允许浏览器弹出窗口以预览小票')
        return
      }
      w.document.open()
      w.document.write(html)
      w.document.close()
    },
    async openPreview() {
      this.previewLoading = true
      try {
        const html = await this.fetchHtml(
          `/admin/receipt-config/preview-custom?printer=${this.activePrinter}`
        )
        this.openHtmlWindow(html)
      } catch (e) {
        this.$message.error(formatApiError(e, '预览失败'))
      } finally {
        this.previewLoading = false
      }
    },
    async browserPrint() {
      this.browserLoading = true
      try {
        const html = await this.fetchHtml('/admin/print/receipt-html/custom')
        this.openHtmlWindow(html)
      } catch (e) {
        this.$message.error(formatApiError(e, '生成打印页失败'))
      } finally {
        this.browserLoading = false
      }
    },
    async cloudPrint(printer) {
      this.printing = true
      try {
        const path =
          printer === 'feie' ? '/admin/print/feie/custom' : '/admin/print/jolimark/custom'
        const resp = await this.$api.post(path, {
          fields: this.buildPayloadFields(),
          copies: this.copies,
          printer
        })
        if (resp && resp.code === 200) {
          this.$message.success(
            resp.message || (printer === 'feie' ? '已提交飞鹅云打印' : '已提交映美云打印')
          )
        } else {
          this.$message.error((resp && resp.message) || '打印失败')
        }
      } catch (e) {
        this.$message.error(
          formatApiError(e, printer === 'feie' ? '飞鹅云打印失败' : '映美云打印失败')
        )
      } finally {
        this.printing = false
      }
    }
  }
}
</script>

<style scoped>
.receipt-custom-print {
  padding: 0 4px 24px;
}
.receipt-page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}
.receipt-page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  font-family: var(--app-font);
}
.receipt-page-desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: #64748b;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.btn-feie {
  background: #2bb673 !important;
  border-color: #2bb673 !important;
}
.printer-tabs {
  margin-bottom: 8px;
}
.receipt-alert {
  margin-bottom: 16px;
}
.receipt-panel {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px;
  padding: 18px 20px 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 28px rgba(15, 23, 42, 0.06);
}
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.panel-head h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.print-form {
  margin-top: 8px;
}
</style>
